// src/redux/slices/userSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// 📌 Fetch all users
export const fetchUsers = createAsyncThunk(
  "users/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/all-users");
      console.log("users data", data)
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);

// 📌 Fetch all admins
export const fetchAdmins = createAsyncThunk(
  "users/fetchAdmins",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/all-admins");
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);

// 📌 Get single user by ID
export const fetchSingleUser = createAsyncThunk(
  "users/fetchSingle",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/single-user/${id}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);

// 📌 Update user
export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/update-user/${id}`, userData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);

// 📌 Update admin
export const updateAdmin = createAsyncThunk(
  "users/updateAdmin",
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/update-admin/${id}`, userData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);


// 📌 Delete user
export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/delete-user/${id}`);
      return { ...data, id };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);


// 📌 Delete admin
export const deleteAdmin = createAsyncThunk(
  "users/deleteAdmin",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/delete-admin/${id}`);
      return { ...data, id };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);

export const fetchLeaderboard = createAsyncThunk(
  "users/fetchLeaderboard",
  async (
    { page = 1, limit = 10, search = "", type = "user" } = {},
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(
        `/admin/leaderboard?page=${page}&limit=${limit}&search=${search}&type=${type}`
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch leaderboard"
      );
    }
  }
);

const initialState = {
  users: [],
  admins: [],
  leaderboard: [],

  leaderboardPagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  },

  selectedUser: null,
  loading: false,
  error: null,
  success: null,
};

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearUserMessages: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.users = action.payload.data;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // Fetch Admins
      .addCase(fetchAdmins.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.admins = action.payload.data;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // Fetch Single User
      .addCase(fetchSingleUser.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.selectedUser = action.payload.data;
        } else {
          state.error = action.payload.message;
        }
      })

      // Update User
      .addCase(updateUser.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.users = state.users.map((u) =>
            u._id === action.payload.data._id ? action.payload.data : u
          );
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })

      // Update Admin
      .addCase(updateAdmin.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.admins = state.admins.map((a) =>
            a._id === action.payload.data._id ? action.payload.data : a
          );
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })

      // Delete User
      .addCase(deleteUser.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.users = state.users.filter((u) => u._id !== action.payload.id);
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })

      // Delete Admin
      .addCase(deleteAdmin.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.admins = state.admins.filter((a) => a._id !== action.payload.id);
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })

      // Leaderboard
      // Leaderboard
      .addCase(fetchLeaderboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchLeaderboard.fulfilled, (state, action) => {
        state.loading = false;

        if (action.payload.success) {
          state.leaderboard = action.payload.data;
          state.leaderboardPagination = action.payload.pagination;
        } else {
          state.error = action.payload.message;
        }
      })

      .addCase(fetchLeaderboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })


  },
});

export const { clearUserMessages } = userSlice.actions;
export default userSlice.reducer;
